import { DealResponse } from '@business/entities/deal';
import { CommonService, getFieldValueString, IntegrationService } from '@business/services';
import { Formatter } from '@core/utils/formatter';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { ContactEmailText } from '@modules/common/ContactEmailText';
import { HasBilling } from '@modules/common/HasBilling';
import { PhoneActions } from '@modules/common/PhoneActions';
import { openSelectContactModal } from '@modules/common/SelectContactModal';
import { openSelectDateModal } from '@modules/common/SelectDateModal';
import { TextBlock } from '@modules/common/TextBlock';
import React, { Fragment, memo, useCallback, useMemo } from 'react';
import { Badge, Card, CardBody } from 'reactstrap';
import { DealOverlayBloc } from './DealOverlayBloc';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { useCompany } from '@core/utils/hooks/misc';
import { IntegrationType } from '@business/entities/integration';
import { openSelectVendastaAccountModal } from '@modules/common/SelectVendastaAccountModal';
import { openOrganizationOverlay } from '@modules/organizations/OrganizationOverlay';
import { FieldType } from '@business/entities/settingFields';
import { useObservable } from '@core/utils/hooks/rxjs';

export interface DealDetailsProps {
  deal: DealResponse;
  bloc: DealOverlayBloc;
}

export const DealDetails = memo((props: DealDetailsProps) => {
  const { deal, bloc } = props;
  const users = useObservable(bloc.users$);
  const settings = useSettings();
  const company = useCompany();

  const customFields = useMemo(() => {
    if (!settings) return [];
    return R.pipe(
      settings.customFields,
      R.ops.filter(f => f.for === 'deals'),
      R.ops.map(field => ({
        id: field.id,
        title: field.label,
        value: getFieldValueString(field, deal.customFields[field.id] || ''),
        field,
      })),
      R.ops.filter(item => !R.isEmpty(item.value)),
    );
  }, [deal.customFields, settings]);

  const onAssignContact = useCallback(() => {
    openSelectContactModal({
      onSelect: contactId => bloc.updateDealAction(deal.id, contactId, 'contact_id'),
      title: 'Select Contact',
      button: 'Update',
      orgId: deal.organization.id,
      contactId: deal.contact ? deal.contact.id : undefined,
      email: false,
      phone: false,
    });
  }, [bloc, deal]);

  const customFieldContent = useMemo(() => {
    return R.chunk(customFields, 5).map((fields, fIndex) => (
      <div key={fIndex} className="d-flex flex-wrap mb-4 project-line">
        {fields.map(field => {
          if (field.field.type === FieldType.user) {
            const fieldValue = field.value;
            const user = R.find(users, u => R.toString(u.id) === R.toString(fieldValue));
            return (
              <TextBlock key={field.id} title={field.title}>
                {CommonService.getFullName(user)}
              </TextBlock>
            );
          }
          return (
            <TextBlock key={field.id} title={field.title}>
              {field.value}
            </TextBlock>
          );
        })}
        {fields.length < 5 && R.times(5 - fields.length, R.constant(<div style={{ flex: 1 }}></div>))}
      </div>
    ));
  }, [customFields, users]);

  const executiveSummary = useMemo(() => {
    if (IntegrationService.isServiceEnabled(IntegrationType.vendasta)) {
      const hasReport = !!deal.organization.extra.accountGroupId;
      return (
        <TextBlock
          title={
            <Fragment>
              Snapshot Report
              <a
                className="text-primary"
                onClick={() =>
                  openSelectVendastaAccountModal({
                    type: 'deal',
                    objectId: deal.id,
                    boardId: deal.organization.id,
                    value:
                      deal.organization.vendastaName && deal.organization.vendastaId
                        ? { companyName: deal.organization.vendastaName, accountGroupId: deal.organization.vendastaId }
                        : undefined,
                  })
                }
              >
                <i className={`bx bx-edit ml-2 text-primary`}></i>
              </a>
            </Fragment>
          }
        >
          {hasReport && (
            <a
              className=""
              href={hasReport ? R.get(deal, 'organization.extra.reportUrl') ?? undefined : undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open
              <i className={`fas fa-external-link-alt ml-2 text-primary`}></i>
            </a>
          )}
          {!hasReport && (
            <a
              onClick={() =>
                openSelectVendastaAccountModal({
                  type: 'deal',
                  objectId: deal.id,
                  boardId: deal.organization.id,
                  value:
                    deal.organization.vendastaName && deal.organization.vendastaId
                      ? { companyName: deal.organization.vendastaName, accountGroupId: deal.organization.vendastaId }
                      : undefined,
                })
              }
              className="text-primary"
            >
              Select
            </a>
          )}
        </TextBlock>
      );
    }
    return <div />;
  }, [deal]);

  const intervalValue = useMemo(() => {
    if (deal.interval === 'one time') {
      return '';
    } else {
      return ' / ' + deal.interval;
    }
  }, [deal]);

  return (
    <Card>
      <CardBody>
        <div className="detail-overlay">
          <div className="d-flex flex-wrap mb-4 project-line">
            <TextBlock title={gcLabel('Organization')}>
              <a className="text-primary" onClick={() => openOrganizationOverlay({ orgId: deal.orgId })}>
                {deal.organization.name}
              </a>
            </TextBlock>
            <TextBlock
              title={
                <>
                  {gcLabel('Contact')}
                  <a className="ml-2">
                    <i className="bx bx-edit font-size-16" onClick={onAssignContact}></i>
                  </a>
                </>
              }
            >
              {deal.contact ? (
                <>
                  <a href={`/people/${deal.contact.id}`}>{CommonService.getFullName(deal.contact)}</a>
                  {!!deal.contact.email && (
                    <Fragment>
                      <br />
                      <ContactEmailText org={deal.organization} email={deal.contact.email} />
                    </Fragment>
                  )}
                  {!!deal.contact.phone && (
                    <div className="d-flex align-items-center">
                      {deal.contact.phone} {deal.contact.phoneExt ? 'Ext: ' + deal.contact.phoneExt : ''}
                      <PhoneActions phone={deal.contact.phone} objectId={deal.id} objectType="deal" contactId={deal.contact.id} />
                    </div>
                  )}
                </>
              ) : (
                <a className="text-primary" onClick={onAssignContact}>
                  Select Contact
                </a>
              )}
            </TextBlock>
            {!deal.noValue && (
              <>
                <HasBilling>
                  <TextBlock title="Value">
                    {Formatter.formatCurrency(deal.valueDefault, company.currencyCode)}
                    {intervalValue}
                  </TextBlock>
                </HasBilling>
              </>
            )}

            <TextBlock title="Expected Closed Date">
              <div className="d-flex flex-row">
                <span>{Formatter.formatTime(deal.expectedClosed)}</span>
                <a
                  className="d-flex ml-2"
                  onClick={() =>
                    openSelectDateModal({
                      title: 'Update Expected Closed Date',
                      initialValue: deal.expectedClosed,
                      minDate: toMoment().unix(),
                      onSubmit: date => bloc.updateClosedDate(deal.id, date),
                    })
                  }
                >
                  <i className="bx bx-calendar font-size-16 text-primary"></i>
                </a>
              </div>
            </TextBlock>
            <TextBlock title="Next Contact Date">
              <div className="d-flex flex-row">
                {deal.nextContactDate && <span className="mr-2">{Formatter.formatTime(deal.nextContactDate)}</span>}
                <a
                  className="d-flex"
                  onClick={() =>
                    openSelectDateModal({
                      title: 'Update Next Contact Date',
                      initialValue: deal.nextContactDate,
                      onSubmit: date => bloc.updateNextContactDate(deal.id, date),
                    })
                  }
                >
                  <i className="bx bx-calendar font-size-16 text-primary"></i>
                </a>
              </div>
            </TextBlock>
            {executiveSummary}
          </div>
          {customFieldContent}
        </div>
      </CardBody>
    </Card>
  );
});
